import {annotateHTML} from '@frontapp/html-highlights-transformer';
import React, {FC, ReactNode, useMemo} from 'react';
import styled, {css, DefaultTheme} from 'styled-components';

import {Trans} from '../../../../../../../libs/shared/core/src/helpers/internationalization/react';
import {iSolemnlySwearThisHtmlIsClean} from '../../../../../../../libs/shared/core/src/helpers/typescript/cleanHtml';
import {QualBotResponse} from '../../../../../../../libs/shared/core/src/models/api/chatBotTranscriptResponseModel';
import {
  LoadedMessageModel,
  messageHasQualBotsResponses,
} from '../../../../../../../libs/shared/core/src/models/api/messageModel';
import {getHasNewSynchronousP3} from '../../../../../../../libs/shared/core/src/store/featureFlags/featureFlagsSelectors';
import {TimelineVariationsEnum} from '../../../../../../../libs/shared/core/src/store/timelineVariations/timelineVariationsTypes';
import {TooltipCoordinator} from '../../../../../../../libs/shared/ui/src/components/tooltips/tooltipCoordinator';
import {TooltipIconWithHover} from '../../../../../../../libs/shared/ui/src/components/tooltips/tooltipIconWithHover';
import {fontSizes, fontWeights} from '../../../../../../../libs/shared/ui/src/styles/fontStyles';
import {textSelection} from '../../../../../../../libs/shared/ui/src/styles/mixins';
import {ThemeTypesEnum} from '../../../../../../../libs/shared/ui/src/theme/themeTypes';
import {useAppSelector} from '../../../../../../helpers/appReduxHelpers';
import {maybeRenderQualBotAnswerLabel} from '../../../../../../helpers/messageViewerChatHelpers';
import {filterHighlightAnnotationsForMessageContent} from '../../../../../../helpers/models/annotationHelpers';
import {AnnotationSourcesEnum} from '../../../../../../models/api/annotationTypes';
import {AnnotationsHighlightViewerInheritableProps} from '../../../../../common/annotation/annotationsHighlightViewer';
import {AnnotationsHighlightViewerContainer} from '../../../../../common/annotation/annotationsHighlightViewerContainer';
import {buildAnnotationCloseTag, buildAnnotationTagOpen} from '../helpers/annotationTagsHelper';
import {MessageViewerBody} from '../messageViewerBody';

/*
 * Props.
 */

export interface MessageViewerChatBodyProps extends AnnotationsHighlightViewerInheritableProps {
  message: LoadedMessageModel;
  isUserVisitor: boolean;
  className?: string;
  timelineVariation: TimelineVariationsEnum;
  isInbound?: boolean;
  qualBotResponses: ReadonlyArray<QualBotResponse>;
  isUserVerified: boolean;
}

/*
 * Style.
 */

interface StyledChatDivProps {
  $timelineVariation: TimelineVariationsEnum;
  $hasNewSynchronousP3: boolean;
}
const StyledChatDiv = styled.div<StyledChatDivProps>`
  ${textSelection()};

  color: ${(p) => p.theme.greys.shade80};
  font-size: ${fontSizes.medium};

  ${(p) => addChatDivStyles(p.$timelineVariation, p.$hasNewSynchronousP3)}

  .front-inline-img {
    max-width: 60%;
    height: auto;
  }
`;
const addChatDivStyles = ($timelineVariation: TimelineVariationsEnum, $hasNewSynchronousP3: boolean) =>
  $timelineVariation === TimelineVariationsEnum.SYNCHRONOUS && !$hasNewSynchronousP3
    ? css`
        padding: 8px 12px;
        max-width: 500px;
        line-height: 20px;
      `
    : css`
        padding: 12px 16px;
        line-height: 22px;
      `;

const StyledBotTranscriptDiv = styled.div`
  padding: 4px 0;
`;

interface StyledBotTranscriptResponseDivProps {
  $isFieldName?: boolean;
  $isLastItem?: boolean;
  $isMessageField?: boolean;
}

const StyledBotTranscriptResponseDiv = styled.div<StyledBotTranscriptResponseDivProps>`
  ${(p) => addStyledBotTranscriptResponseDivStyles(p)}
`;

const addStyledBotTranscriptResponseDivStyles = (props: StyledBotTranscriptResponseDivProps) => {
  if (!props.$isFieldName) {
    return css`
      margin-bottom: ${props.$isLastItem ? '0px' : '10px'};
      color: ${(p) => p.theme.greys.shade80};
      font-weight: ${fontWeights.normal};
    `;
  }

  return css`
    font-weight: ${fontWeights.semibold};
    margin-bottom: ${props.$isMessageField && !props.$isLastItem ? '10px' : '0px'};
    color: ${(p) =>
      computeShouldUseDarkModeText(p.theme) ? p.theme.constants.white : p.theme.greys.shade90};
  `;
};

// Avoid showing white text on a white background if the user has mixed themes, such as DARK_SIDEBAR.
const computeShouldUseDarkModeText = (theme: DefaultTheme) =>
  (theme.type === ThemeTypesEnum.DARK || theme.type === ThemeTypesEnum.EXPANDED_DARK) &&
  theme.message.background !== theme.constants.white;

const StyledQualBotFieldNameLabelDiv = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const StyledTooltipWrapperDiv = styled.div`
  width: 220px;
  display: flex;
  flex-direction: column;
  gap: 1em;
`;

/*
 * Constants.
 */

const emailCustomFieldName = 'Email';

/*
 * Component.
 */

export const MessageViewerChatBody: FC<MessageViewerChatBodyProps> = (props) => {
  const {annotations, message} = props;

  const hasNewSynchronousP3 = useAppSelector(getHasNewSynchronousP3);

  const bodyWithAnnotations = useMemo(() => {
    const filteredAnnotations = filterHighlightAnnotationsForMessageContent(annotations);
    return annotateHTML(message.body, filteredAnnotations, {
      onAnnotationOpen: (annotation) => buildAnnotationTagOpen(annotation),
      onAnnotationClose: (_annotation) => buildAnnotationCloseTag(),
    });
  }, [annotations, message.body]);

  return (
    <AnnotationsHighlightViewerContainer
      annotations={props.annotations}
      onQuoteInCommentClick={props.onQuoteInCommentClick}
      hasMessageAnnotations={props.hasMessageAnnotations}
      panelId={props.panelId}
      conversationId={props.message.conversationId}
      annotationSource={AnnotationSourcesEnum.LIGHT_BODY}
    >
      <StyledChatDiv
        className={props.className}
        $timelineVariation={props.timelineVariation}
        $hasNewSynchronousP3={hasNewSynchronousP3}
      >
        <MessageViewerBody
          {...props}
          trustedHtml={iSolemnlySwearThisHtmlIsClean(bodyWithAnnotations)}
          messageId={props.message.id}
          attachments={props.message.attachments}
          isExternalContentHidden={false}
          isUserVisitor={props.isUserVisitor}
          onQuoteInCommentClick={props.onQuoteInCommentClick}
          timelineVariation={props.timelineVariation}
          isInbound={props.isInbound}
        >
          {(messageBody) => maybeAddBotTranscript(props, messageBody)}
        </MessageViewerBody>
      </StyledChatDiv>
    </AnnotationsHighlightViewerContainer>
  );
};

/*
 * Helpers.
 */

function maybeAddBotTranscript(props: MessageViewerChatBodyProps, messageBody: ReactNode): ReactNode {
  if (messageHasQualBotsResponses(props.message)) {
    return renderQualBotsResponsesAsBody(props, messageBody);
  }

  return messageBody;
}

const renderQualBotFieldName = (qualBotResponse: QualBotResponse, props: MessageViewerChatBodyProps) => {
  const shouldShowVerifiedTooltip =
    props.isUserVerified && qualBotResponse.fieldName === emailCustomFieldName;

  return (
    <StyledQualBotFieldNameLabelDiv>
      <StyledBotTranscriptResponseDiv $isFieldName>
        {qualBotResponse.fieldName}
      </StyledBotTranscriptResponseDiv>
      {shouldShowVerifiedTooltip && (
        <TooltipCoordinator
          render={() => (
            <StyledTooltipWrapperDiv>
              <Trans id="message-viewer-chat-message-verified-user-tooltip">
                This information is automatically collected because the chat visitor is logged in. The chatbot
                skipped asking this question since it is known.
              </Trans>
            </StyledTooltipWrapperDiv>
          )}
        >
          <TooltipIconWithHover />
        </TooltipCoordinator>
      )}
    </StyledQualBotFieldNameLabelDiv>
  );
};

const renderQualBotResponse = (
  qualBotResponse: QualBotResponse,
  props: MessageViewerChatBodyProps,
  isLastItem?: boolean,
) => (
  <>
    {renderQualBotFieldName(qualBotResponse, props)}

    {/* A QualBot response can have `isLastItem: true` if it is rendered within a DecisionTree response. Otherwise, this is always false. */}
    <StyledBotTranscriptResponseDiv $isLastItem={isLastItem}>
      {maybeRenderQualBotAnswerLabel(qualBotResponse)}
    </StyledBotTranscriptResponseDiv>
  </>
);

const renderQualBotsResponsesAsBody = (props: MessageViewerChatBodyProps, messageBody: ReactNode) => {
  const {qualBotResponses} = props;

  return (
    <StyledBotTranscriptDiv>
      {qualBotResponses?.map((item) => renderQualBotResponse(item, props))}
      {renderTranscriptMessageSection(props, messageBody)}
    </StyledBotTranscriptDiv>
  );
};

function renderTranscriptMessageSection(props: MessageViewerChatBodyProps, messageBody: ReactNode) {
  return (
    <>
      <StyledBotTranscriptResponseDiv $isFieldName>
        <Trans id="message-viewer-chat-message-label">Message</Trans>
      </StyledBotTranscriptResponseDiv>
      {/* The body from the message itself already has HTML applied. */}
      <StyledBotTranscriptResponseDiv $isLastItem>{messageBody}</StyledBotTranscriptResponseDiv>
    </>
  );
}
